import { QuestionnaireResponse, QuestionnaireResponseItemAnswer } from "@medplum/fhirtypes";

import { isNumber } from "lodash";

import { TemperatureUnitToCode, VITAL_SIGNS_FORM_ID } from "@/encounters/constants/vital-sign";
import { WeightUnitToCode } from "@/fhir/weight";
import { convertFeetAndInchesToInches } from "@/utils/ucum_units";
import {
  qResponseCoding,
  qResponseGroup,
  qResponseInteger,
  qResponseQuantity,
  qResponseString,
  toQuestionnaireResponse,
} from "@/utils/zod-questionnaires";

export const BodyHeightUnitToCode = {
  // https://hl7.org/fhir/R4/valueset-ucum-bodylength.html
  in: "[in_i]",
  cm: "cm",
} as const;
export const BodyHeightUnitChoices = {
  // "Feet and Inches" is not a valid FHIR code, but it's used in the UI.
  // The value is converted to inches before saving it.
  "Feet and Inches": "in",
  Centimeters: "cm",
} as const;

export function generateVitalSignsQuestionnaireResponse(
  form: Record<string, QuestionnaireResponseItemAnswer>,
): QuestionnaireResponse {
  function getHeightValue(
    form: Record<string, QuestionnaireResponseItemAnswer>,
  ): number | undefined {
    if (!form.encounterVitalSignsHeightUnit?.valueString) return undefined;

    const unit = form.encounterVitalSignsHeightUnit.valueString;

    if (
      unit === BodyHeightUnitChoices["Feet and Inches"] &&
      (isNumber(form.encounterVitalSignsHeightFeet?.valueDecimal) ||
        isNumber(form.encounterVitalSignsHeightInches?.valueDecimal))
    ) {
      return convertFeetAndInchesToInches(
        isNumber(form.encounterVitalSignsHeightFeet?.valueDecimal)
          ? form.encounterVitalSignsHeightFeet.valueDecimal
          : 0,
        isNumber(form.encounterVitalSignsHeightInches?.valueDecimal)
          ? form.encounterVitalSignsHeightInches.valueDecimal
          : 0,
      );
    } else if (unit === BodyHeightUnitChoices.Centimeters) {
      return form.encounterVitalSignsHeightCm?.valueDecimal;
    }
    return undefined;
  }

  const heightValue = getHeightValue(form);

  const questionnaireResponse: QuestionnaireResponse = toQuestionnaireResponse(form, {
    weight: (form, fieldName) => {
      const weight = form.encounterVitalSignsWeight?.valueDecimal;
      const unit = form.encounterVitalSignsWeightUnit?.valueString as keyof typeof WeightUnitToCode;
      return isNumber(weight) && unit
        ? qResponseQuantity({
            fieldName,
            code: "29463-7",
            display: "Body weight",
            value: weight,
            valueUnit: unit,
            valueCode: WeightUnitToCode[unit],
          })
        : null;
    },
    height: (form, fieldName) => {
      const unit = form.encounterVitalSignsHeightUnit
        ?.valueString as keyof typeof BodyHeightUnitToCode;
      return isNumber(heightValue) && unit
        ? qResponseQuantity({
            fieldName,
            code: "8302-2",
            display: "Body height",
            value: heightValue,
            valueUnit: unit,
            valueCode: BodyHeightUnitToCode[unit],
          })
        : null;
    },
    heartRate: (form, fieldName) => {
      const rate = form.encounterVitalSignsHeartRate?.valueDecimal;
      return isNumber(rate)
        ? qResponseQuantity({
            fieldName,
            code: "8867-4",
            display: "Heart Rate",
            value: rate,
            valueUnit: "beats/minute",
            valueCode: "/min",
          })
        : null;
    },
    respiratoryRate: (form, fieldName) => {
      const rate = form.encounterVitalSignsRespiratoryRate?.valueDecimal;
      return isNumber(rate)
        ? qResponseQuantity({
            fieldName,
            code: "9279-1",
            display: "Respiratory rate",
            value: rate,
            valueUnit: "breaths/minute",
            valueCode: "/min",
          })
        : null;
    },
    temperature: (form, fieldName) => {
      const temp = form.encounterVitalSignsTemperature?.valueDecimal;
      const method = form.encounterVitalSignsTemperatureMethod?.valueCoding;
      const unit = form.encounterVitalSignsTemperatureUnit
        ?.valueString as keyof typeof TemperatureUnitToCode;
      return isNumber(temp) && unit && method
        ? qResponseGroup({
            parentResponseItem: qResponseQuantity({
              fieldName,
              code: "8310-5",
              display: "Body temperature",
              value: temp,
              valueUnit: unit,
              valueCode: TemperatureUnitToCode[unit],
            }),
            components: [
              qResponseCoding({
                fieldName: `${fieldName}Method`,
                code: "8331-1",
                display: "Temperature method",
                value: method,
              }),
            ],
          })
        : null;
    },
    comments: (form, fieldName) => {
      const value = form.encounterVitalSignsVitalsComments?.valueString;
      return value
        ? qResponseString({
            fieldName,
            code: `forms/${VITAL_SIGNS_FORM_ID}/comments`,
            display: "Comments",
            value,
          })
        : null;
    },
    systolicBP: (form, fieldName) => {
      const value = form.encounterVitalSignsSystolicBP?.valueDecimal;
      return isNumber(value)
        ? qResponseInteger({
            fieldName,
            code: "8480-6",
            display: "Systolic blood pressure",
            value,
          })
        : null;
    },
    diastolicBP: (form, fieldName) => {
      const value = form.encounterVitalSignsDiastolicBP?.valueDecimal;
      return isNumber(value)
        ? qResponseInteger({
            fieldName,
            code: "8462-4",
            display: "Diastolic blood pressure",
            value,
          })
        : null;
    },
  });

  return questionnaireResponse;
}
